import React from 'react';
import { StyledForgeContainer } from 'view/forge-ui/forge-runner/styled';
import { Headline } from 'view/forge-ui/request-details-main-section';
import { FORGE_HELP_CENTER } from '@atlassian/help-center-common-component/forge-ui/constants';
import { ExtensionPointProvider } from '@atlassian/help-center-common-component/forge-ui/extension-point-provider';
import { ExtensionProvider } from '@atlassian/help-center-common-component/forge-ui/extension-provider';
import { Renderer } from '@atlassian/help-center-common-component/forge-ui/renderer';
import type {
    Extension,
    ExtensionData,
    ForgePageName,
    ForgeRunnerProps,
    ForgeRunnerPropsWithExtension,
    RunnerExtensionData,
    WithBlockedAppsBannerProps,
} from '@atlassian/help-center-common-component/forge-ui/types';
import { withExtensionList } from '@atlassian/help-center-common-component/forge-ui/with-extension-list';
import { useShowBanner } from '@atlassian/help-center-common-util/blocked-apps-banner';

export const ForgeRunnerReactFragment: React.FC<ForgeRunnerPropsWithExtension> = ({
    extension,
    type,
    page,
    portalId,
    requestKey,
    requestTypeId,
    noWrapper,
}) => {
    const pages = extension.properties.pages || [];

    const extensionData: ExtensionData = React.useMemo(() => {
        const computedExtensionData: RunnerExtensionData = {
            page,
            type,
        };

        if (!!portalId) {
            computedExtensionData.portal = { id: portalId };
        }

        if (requestKey || requestTypeId) {
            computedExtensionData.request = {
                ...(requestKey && { key: requestKey }),
                ...(requestTypeId && { typeId: requestTypeId }),
            };
        }

        return computedExtensionData;
    }, [page, portalId, requestKey, requestTypeId, type]);

    return pages.includes(page) || pages.length === 0 ? (
        <React.Fragment key={extension.id}>
            <Headline>{extension.properties.title}</Headline>
            <ExtensionProvider extension={extension} noWrapper={noWrapper}>
                <Renderer extension={extension} extensionData={extensionData} />
            </ExtensionProvider>
        </React.Fragment>
    ) : null;
};

interface WrapperProps {
    page: ForgePageName;
    noWrapper?: boolean;
}

export const WithWrapper: React.FC<React.PropsWithChildren<WrapperProps>> = ({ page, children, noWrapper }) => {
    if (page === FORGE_HELP_CENTER) {
        return <StyledForgeContainer noWrapper={!!noWrapper}>{children}</StyledForgeContainer>;
    }
    return <>{children}</>;
};

export const ForgeRunnerModuleDumb: React.FC<ForgeRunnerProps & WithBlockedAppsBannerProps> = ({
    page,
    portalId,
    projectId,
    requestKey,
    requestTypeId,
    type,
    extensions,
    showBanner,
    resetBannerState,
    noWrapper,
}) => {
    const allowedExtensions: Extension[] = useShowBanner(extensions, page, true, showBanner, resetBannerState);

    return (
        <>
            {allowedExtensions.length > 0 && (
                <ExtensionPointProvider extensionType={type} noWrapper={noWrapper}>
                    {allowedExtensions.map(
                        (extension: Extension) =>
                            extension && (
                                <WithWrapper page={page} key={`${extension.id}-wrapper`} noWrapper={!!noWrapper}>
                                    <ForgeRunnerReactFragment
                                        key={extension.id}
                                        type={type}
                                        page={page}
                                        portalId={portalId}
                                        projectId={projectId}
                                        requestKey={requestKey}
                                        requestTypeId={requestTypeId}
                                        extension={extension}
                                        noWrapper={noWrapper}
                                    />
                                </WithWrapper>
                            )
                    )}
                </ExtensionPointProvider>
            )}
        </>
    );
};

export const ForgeRunnerModule = withExtensionList()(ForgeRunnerModuleDumb);

export default ForgeRunnerModule;
